/**
 * @fileoverview O resumo da contratação (03/11/2026): cada item com o valor, o
 * total em destaque, o botão da próxima ação e as condições reais da cobrança. Na
 * alteração, o que a Empresa paga hoje, a mensalidade nova e o que se paga agora
 * (a cotação do servidor). No computador, é a coluna da direita, sempre à vista;
 * abaixo de 960 px, é a barra fixa embaixo, com o total e o botão, e os itens
 * abrem por cima.
 */

import React, { useState } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ChevronUp } from "lucide-react";

import { billingErrorText } from "../PlanControls";
import { InfoTip } from "../../../pages/Flows/components/TriageManager/InfoTip";
import { i18n } from "../../../translate/i18n";
import { money, thousands } from "../../../utils/billingPlans";
import { formatUtcDate } from "../../../utils/formatLocaleDate";

const t = (key, options) => i18n.t(`plansDialog.${key}`, options);

const NOTHING_TO_CHANGE = "ERR_BILLING_NOTHING_TO_CHANGE";

const itemName = (item) => (item.code === "plan" ? item.name : item.code === "internal_chat" ? t("internal") : t("aiPack"));
const itemDetail = (item) =>
  item.code === "ai_pack" ? t("linePack", { messages: thousands(item.messages) }) : t("lineSeats", { count: item.quantity, price: money(item.unitCents) });

/** As condições da cobrança, em poucas linhas: na coluna do resumo ou, sem ela, no fim da etapa. */
export const TrustNotes = ({ classes, notes, className = "" }) => (
  <ul className={`${classes.trust} ${className}`}>
    {notes.map(({ icon: Icon, text }) => (
      <li key={text} className={classes.trustItem}>
        <Icon aria-hidden="true" />
        <span>{text}</span>
      </li>
    ))}
  </ul>
);

const OrderSummary = ({ classes, compact, items, total, changeMode, quote, quoting, quoteError, currentMonthlyCents, dueNow, freeChange, onRetryQuote, primary, note, formError, notes }) => {
  const [open, setOpen] = useState(false);
  // Recalculando, o valor anterior fica esmaecido (sem pular de altura a cada clique).
  const busy = quoting && !!quote;

  const lines = (
    <ul className={classes.lines}>
      {items.map((item) => (
        <li key={item.code} className={classes.lineRow}>
          <span className={classes.lineTop}>
            <span>{itemName(item)}</span>
            <span className={classes.lineValue}>{money(item.quantity * item.unitCents)}</span>
          </span>
          <span className={classes.lineDetail}>{itemDetail(item)}</span>
        </li>
      ))}
    </ul>
  );

  const quoteAlert = changeMode && !quote && !quoting && quoteError && quoteError !== NOTHING_TO_CHANGE && (
    <div className={classes.quoteAlert} role="alert">
      <p className={classes.formError}>{billingErrorText(quoteError)}</p>
      <button type="button" className={classes.textLink} style={{ alignSelf: "flex-start" }} onClick={onRetryQuote}>
        {t("quoteRetry")}
      </button>
    </div>
  );

  // Na alteração: hoje, a mensalidade nova, e os estados da cotação (calculando, falhou, nada mudou).
  const quoteDetails = changeMode && (
    <>
      {(currentMonthlyCents !== null || quote) && (
        <div className={`${classes.quoteRows} ${busy ? classes.quoteRowsBusy : ""}`}>
          {currentMonthlyCents !== null && (
            <span className={classes.quoteRow}>
              <span>{t("quoteTodayLabel")}</span>
              <span className={classes.lineValue}>{money(quote ? quote.currentMonthlyCents : currentMonthlyCents)}</span>
            </span>
          )}
          {quote && (
            <span className={classes.quoteRow}>
              <span>{t("quoteMonthly", { date: formatUtcDate(quote.nextBillingAt) })}</span>
              <span className={classes.lineValue}>{money(quote.nextMonthlyCents)}</span>
            </span>
          )}
        </div>
      )}
      {!quote && quoting && (
        <div className={classes.quoteWaiting}>
          <CircularProgress size={16} color="inherit" aria-label={t("quoting")} />
          <span>{t("quoting")}</span>
        </div>
      )}
      {!compact && quoteAlert}
      {!quote && !quoting && (!quoteError || quoteError === NOTHING_TO_CHANGE) && <p className={classes.quoteNothing}>{t("quoteNothing")}</p>}
    </>
  );

  const caption = changeMode ? t("quoteNow") : t("total");
  const amount = changeMode ? (quote ? (freeChange ? t("quoteFreeValue") : money(dueNow)) : "") : money(total);
  const actions = (
    <div className={classes.actionBox}>
      {formError && (
        <p className={classes.formError} role="alert">
          {formError}
        </p>
      )}
      <button type="button" className={classes.cta} onClick={primary.onClick} disabled={primary.disabled}>
        {primary.busy && <CircularProgress size={16} color="inherit" />}
        {primary.label}
      </button>
      {note && <p className={classes.ctaNote}>{note}</p>}
    </div>
  );

  if (compact) {
    // Na alteração sem cotação (nada mudou ainda), a barra mostra o que a Empresa paga hoje.
    const noQuoteYet = changeMode && !quote;
    const barCaption = noQuoteYet && !quoting ? t("quoteTodayLabel") : caption;
    const barAmount = noQuoteYet ? (quoting ? t("quoting") : money(currentMonthlyCents)) : amount;
    return (
      <aside className={classes.bar} aria-label={changeMode ? t("summaryTitleChange") : t("summaryTitle")}>
        {open && (
          <div className={classes.barDetails} id="plans-summary-details">
            {lines}
            {quoteDetails}
          </div>
        )}
        <button type="button" className={classes.barToggle} onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-controls={open ? "plans-summary-details" : undefined}>
          <span className={`${classes.barCaption} ${open ? classes.barChevronOpen : ""}`}>
            {barCaption}
            <ChevronUp aria-hidden="true" />
          </span>
          <span className={`${classes.barAmount} ${busy ? classes.quoteRowsBusy : ""}`} aria-live="polite">
            {barAmount}
          </span>
        </button>
        {changeMode && quote && <p className={classes.barHint}>{freeChange ? t("quoteFree") : t("quoteNowHint", { count: quote.remainingDays })}</p>}
        {quoteAlert}
        {actions}
      </aside>
    );
  }

  return (
    <aside className={classes.summaryPanel} aria-labelledby="plans-dialog-summary">
      <h3 className={classes.summaryHeading} id="plans-dialog-summary">
        {changeMode ? t("summaryTitleChange") : t("summaryTitle")}
      </h3>
      {lines}
      {quoteDetails}
      {amount && (
        <div className={`${classes.total} ${busy ? classes.quoteRowsBusy : ""}`} aria-live="polite" aria-busy={quoting}>
          <span className={classes.totalCaption}>
            {caption}
            {changeMode && <InfoTip text={t("quoteNowInfo")} />}
          </span>
          <span className={classes.totalAmount} key={amount}>
            {amount}
          </span>
          {changeMode && quote && <span className={classes.totalHint}>{t("quoteNowHint", { count: quote.remainingDays })}</span>}
          {freeChange && <p className={classes.quoteNote}>{t("quoteFree")}</p>}
        </div>
      )}
      {actions}
      {notes && notes.length > 0 && <TrustNotes classes={classes} notes={notes} />}
    </aside>
  );
};

export default OrderSummary;
